import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from 'react-bootstrap';
import AuthCard from '../components/AuthCard';
import { HISTORY_IMAGES, historyImageSrc } from '../lib/historyImages';

const HERO_FOUNDATIONS = [
  { color: 'yellow', value: 4 },
  { color: 'blue', value: 7 },
  { color: 'green', value: 2 },
  { color: 'red', value: 11 }
];

const HERO_COLUMNS = [
  [
    { color: 'red', value: 13 },
    { color: 'blue', value: 9 }
  ],
  [{ color: 'green', value: 12 }],
  [
    { color: 'yellow', value: 10 },
    { color: 'green', value: 8 },
    { color: 'red', value: 5 }
  ],
  []
];

const WHAT_IT_IS = [
  {
    color: 'yellow',
    glyph: '52',
    title: 'A deck without cards',
    body:
      'Fifty-two square counters in four colors, numbered 1 to 13. No kings, no queens, no aces — which is exactly why a card-shy John D. Rockefeller played it after dinner every night.'
  },
  {
    color: 'green',
    glyph: '13',
    title: 'One simple goal',
    body:
      'Build four foundations in order from 1 up to 13. When the deck runs out, the top counter on each foundation is your score. A perfect game is 52.'
  },
  {
    color: 'red',
    glyph: '?',
    title: 'Every draw is a decision',
    body:
      'Counters arrive in random order. Play each one onto a foundation if it fits, or hold it in one of four columns — but never bury a small number under a large one.'
  }
];

const HOW_TO_PLAY = [
  {
    step: 1,
    color: 'yellow',
    title: 'A counter is called',
    body: 'The deck is shuffled and counters are dealt one at a time. You see one counter and must place it before the next.'
  },
  {
    step: 2,
    color: 'blue',
    title: 'Foundation or column?',
    body: 'If it continues a foundation (a 6 on a 5), play it there. Otherwise tuck it into a column — only the last counter in a column can move up later.'
  },
  {
    step: 3,
    color: 'red',
    title: 'Build to 13',
    body: 'Every 1 starts a new foundation automatically, and any counter that becomes playable jumps up on its own. Higher tops, higher score.'
  }
];

const WAYS_TO_PLAY = [
  {
    color: 'yellow',
    step: 1,
    title: 'Against the computer',
    body: 'Four difficulty levels, from a forgiving Easy to an Expert that plans several draws ahead. Same deck, side by side.'
  },
  {
    color: 'blue',
    step: 2,
    title: 'Live with friends',
    body: 'Open a table and play in real time. Everyone at the table sees the same counters as they are called.'
  },
  {
    color: 'red',
    step: 3,
    title: 'Challenge anyone',
    body: 'Send a challenge with a fixed deck. Your friend plays the exact same draw whenever they like. Best score wins.'
  }
];

function Login() {
  const [guestOpen, setGuestOpen] = useState(false);

  function goToPlay(openGuest = false) {
    if (openGuest) setGuestOpen(true);
    const target = document.getElementById('play');
    if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  return (
    <div className="landing">
      <header className="site-nav">
        <div className="site-nav-inner">
          <a href="#top" className="brand">
            <span className="brand-mark" aria-hidden="true">
              N
            </span>
            <span className="brand-text">
              <span className="brand-name">Game of Numerica</span>
              <span className="brand-sub">Est. 1890s</span>
            </span>
          </a>
          <nav className="site-links" aria-label="Site">
            <a href="#what">The game</a>
            <a href="#how">How to play</a>
            <a href="#history">History</a>
            <Link to="/rules">Rules</Link>
            <Button size="sm" onClick={() => goToPlay(false)}>
              Play now
            </Button>
          </nav>
        </div>
      </header>

      <main id="top">
        <section className="hero">
          <div className="hero-copy">
            <span className="auth-eyebrow">Est. 1890s · London &amp; Utica, N.Y.</span>
            <h1 className="hero-title">Game of Numerica</h1>
            <p className="hero-lead">
              The Victorian parlor game of numbers. Fifty-two counters, four foundations, one goal: build each from
              1 to 13 before the deck runs out.
            </p>
            <div className="hero-actions">
              <Button size="lg" onClick={() => goToPlay(true)}>
                Play free — no account needed
              </Button>
              <a className="btn btn-outline-primary btn-lg" href="#how">
                How it&rsquo;s played
              </a>
            </div>
            <ul className="hero-facts" aria-label="At a glance">
              <li>Solo, live, or by challenge</li>
              <li>Four computer difficulties</li>
              <li>A game lasts about five minutes</li>
            </ul>
          </div>

          <div className="hero-table" aria-hidden="true">
            <div className="table-felt">
              <p className="zone-label">Foundations</p>
              <div className="stack-row">
                {HERO_FOUNDATIONS.map((tile, idx) => (
                  <div key={idx} className="well foundation">
                    <span className={`counter counter-${tile.color}`}>{tile.value}</span>
                    <span className="foundation-progress">{tile.value}/13</span>
                  </div>
                ))}
              </div>
              <p className="zone-label">Columns</p>
              <div className="column-row">
                {HERO_COLUMNS.map((tiles, idx) => (
                  <div key={idx} className="well column">
                    {tiles.length === 0 && <span className="column-hint">Empty</span>}
                    {tiles.map((tile, tIdx) => (
                      <span key={tIdx} className={`counter counter-${tile.color}`} style={{ zIndex: tIdx + 1 }}>
                        {tile.value}
                      </span>
                    ))}
                  </div>
                ))}
              </div>
            </div>
            <div className="hero-drawn">
              <span className="dock-title">Next counter</span>
              <span className="counter large counter-blue">6</span>
            </div>
          </div>
        </section>

        <section id="what" className="landing-section">
          <div className="section-head">
            <span className="section-kicker">What it is</span>
            <h2>A numbers game that outlasted the century</h2>
            <p>
              Numerica was patented in London in the early 1890s and sold across America by Parker Brothers. It
              plays like solitaire, but every counter tests your foresight.
            </p>
          </div>
          <div className="feature-grid">
            {WHAT_IT_IS.map((item) => (
              <article key={item.title} className="surface-panel feature-tile">
                <span className={`mini-counter counter-${item.color}`} aria-hidden="true">
                  {item.glyph}
                </span>
                <h3>{item.title}</h3>
                <p>{item.body}</p>
              </article>
            ))}
          </div>
        </section>

        <section id="how" className="landing-section">
          <div className="section-head">
            <span className="section-kicker">How to play</span>
            <h2>Three ideas and you&rsquo;re ready</h2>
          </div>
          <ol className="steps">
            {HOW_TO_PLAY.map((item) => (
              <li key={item.step} className="step">
                <span className={`mini-counter counter-${item.color}`} aria-hidden="true">
                  {item.step}
                </span>
                <div>
                  <h3>{item.title}</h3>
                  <p>{item.body}</p>
                </div>
              </li>
            ))}
          </ol>
          <p className="section-footnote">
            Want the original 1894 directions?{' '}
            <Link to="/rules">Read the full rules</Link>.
          </p>
        </section>

        <section id="history" className="landing-section history-band">
          <div className="history-band-inner">
            <div className="history-copy">
              <span className="section-kicker">A game with history</span>
              <h2>Rockefeller&rsquo;s nightly ritual</h2>
              <p>
                John D. Rockefeller never touched a playing card in his life, yet he insisted on Numerica after
                every dinner — with special square counters made so nothing at his table resembled gambling.
                Guests at his Florida estate reported that he rarely lost.
              </p>
              <blockquote>
                &ldquo;Seldom has Mr. Rockefeller faced opponents who could best him at Numerica.&rdquo;
              </blockquote>
              <Link to="/history" className="btn btn-outline-primary">
                Read the full history
              </Link>
            </div>
            <div className="history-photos">
              <div className="auth-showcase">
                <div className="auth-showcase-track">
                  {[...HISTORY_IMAGES, ...HISTORY_IMAGES].map((filename, index) => (
                    <img key={`${filename}-${index}`} src={historyImageSrc(filename)} alt="" />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="ways" className="landing-section">
          <div className="section-head">
            <span className="section-kicker">Ways to play</span>
            <h2>Alone, together, or across the week</h2>
          </div>
          <div className="feature-grid">
            {WAYS_TO_PLAY.map((item) => (
              <article key={item.title} className="surface-panel feature-tile">
                <span className={`mini-counter counter-${item.color}`} aria-hidden="true">
                  {item.step}
                </span>
                <h3>{item.title}</h3>
                <p>{item.body}</p>
              </article>
            ))}
          </div>
        </section>

        <section id="play" className="landing-section play-section">
          <div className="section-head">
            <span className="section-kicker">Play</span>
            <h2>The table is set</h2>
          </div>
          <AuthCard guestOpen={guestOpen} onGuestOpen={() => setGuestOpen(true)} />
        </section>
      </main>

      <footer className="site-footer">
        <div className="site-footer-inner">
          <div className="brand">
            <span className="brand-mark" aria-hidden="true">
              N
            </span>
            <span className="brand-text">
              <span className="brand-name">Game of Numerica</span>
              <span className="brand-sub">A modern tribute to the 1890s original</span>
            </span>
          </div>
          <nav className="site-links" aria-label="Footer">
            <Link to="/rules">Rules</Link>
            <Link to="/history">History</Link>
            <a href="#play">Sign in</a>
          </nav>
        </div>
      </footer>
    </div>
  );
}

export default Login;
